import { useEffect, useState, type JSX } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ScheduleIcon from '@mui/icons-material/Schedule';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { Logger, formatDate, formatAddress } from '@footsteps/shared';
import { Header, Footer, Section } from '../components';
import { SupabaseApiService } from '../components/woz-case-manager/services/supabase-api';
import { routes } from '../routes';

const logger = new Logger('SubmittedPage');

type CaseSummary = {
  addressStreet: string;
  addressHouseNumber: number;
  addressHouseLetter: string | null;
  addressHouseNumberAddition: string | null;
  addressPostalCode: string;
  addressCity: string;
  dateSubmitted: string | null;
  complaintsCount: number;
};

export const Submitted = (): JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [caseSummary, setCaseSummary] = useState<CaseSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      navigate(routes.home, { replace: true });
      return;
    }

    const api = SupabaseApiService.getInstance();
    api
      .fetchCase(id)
      .then((detail) => {
        if (detail.status !== 'submitted') {
          navigate(routes.summary, { replace: true });
          return;
        }
        setCaseSummary({
          addressStreet: detail.addressStreet,
          addressHouseNumber: detail.addressHouseNumber,
          addressHouseLetter: detail.addressHouseLetter ?? null,
          addressHouseNumberAddition: detail.addressHouseNumberAddition ?? null,
          addressPostalCode: detail.addressPostalCode,
          addressCity: detail.addressCity,
          dateSubmitted: detail.dateSubmitted ?? null,
          complaintsCount: detail.complaints?.length ?? 0,
        });
      })
      .catch((err) => {
        logger.error('Failed to fetch submitted case', err);
        navigate(routes.home, { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  return (
    <div className='page-wrapper'>
      <Header />
      <Section paddingY='medium' maxWidth='wide' contentClassName='summary-content'>
        {loading ? (
          <p>Laden...</p>
        ) : caseSummary ? (
          <>
            <div className='submitted-hero'>
              <CheckCircleOutlineIcon sx={{ fontSize: 56, color: 'success.main' }} />
              <h1 className='summary-title'>Bezwaar ingediend</h1>
              <p className='text-body text-muted'>
                Je bezwaar is succesvol ingediend en wordt verwerkt door de gemeente.
              </p>
            </div>

            <div className='card-grid'>
              <div className='card-section card-grid-full'>
                <h2 className='heading-3'>Gegevens bezwaar</h2>
                <div className='info-list'>
                  <div className='info-row'>
                    <span className='text-muted'>Referentie</span>
                    <span className='text-value'>{id}</span>
                  </div>
                  <div className='info-row'>
                    <span className='text-muted'>Adres</span>
                    <span className='text-value'>{formatAddress(caseSummary, { fallback: 'Onbekend' })}</span>
                  </div>
                  {caseSummary.dateSubmitted && (
                    <div className='info-row'>
                      <span className='text-muted'>Ingediend op</span>
                      <span className='text-value'>{formatDate(caseSummary.dateSubmitted)}</span>
                    </div>
                  )}
                  <div className='info-row'>
                    <span className='text-muted'>Aantal klachten</span>
                    <span className='text-value'>{caseSummary.complaintsCount}</span>
                  </div>
                </div>
              </div>

              <div className='card-section card-grid-full notice-card'>
                <div className='notice-card-content'>
                  <InfoOutlinedIcon className='notice-card-icon' />
                  <div>
                    <h2 className='heading-3'>Wat gebeurt er nu?</h2>
                    <p className='text-body'>
                      Je bezwaar is verzonden naar de gemeente. De gemeente neemt je bezwaar in behandeling en
                      beoordeelt of de WOZ-waarde moet worden aangepast. Je ontvangt hierover bericht van de gemeente.
                    </p>
                  </div>
                </div>
              </div>

              <div className='card-section'>
                <div className='submitted-step'>
                  <EmailOutlinedIcon sx={{ color: 'var(--color-primary)', fontSize: 28 }} />
                  <div>
                    <h3 className='heading-3'>Bevestiging per e-mail</h3>
                    <p className='text-body text-muted'>
                      Je ontvangt een bevestiging per e-mail met de details van je ingediende bezwaar.
                    </p>
                  </div>
                </div>
              </div>

              <div className='card-section'>
                <div className='submitted-step'>
                  <ScheduleIcon sx={{ color: 'var(--color-primary)', fontSize: 28 }} />
                  <div>
                    <h3 className='heading-3'>Behandeltermijn</h3>
                    <p className='text-body text-muted'>
                      De gemeente heeft tot eind december van dit jaar de tijd om een beslissing te nemen op je bezwaar. In de praktijk gebeurt dit vaak al eerder
                    </p>
                  </div>
                </div>
              </div>

              <div className='card-section card-grid-full notice-card'>
                <div className='notice-card-content'>
                  <InfoOutlinedIcon className='notice-card-icon' />
                  <div>
                    <h2 className='heading-3'>Bewaartermijn</h2>
                    <p className='text-body'>
                      Na indiening word je bezwaar en alle bijbehorende gegevens na <strong>30 dagen</strong>{' '}
                      automatisch verwijderd uit ons systeem. Zorg ervoor dat je de bevestigingsmail bewaart voor je
                      eigen administratie.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className='card-actions'>
              <Link to={routes.home} className='link-primary'>
                Terug naar home
              </Link>
            </div>
          </>
        ) : null}
      </Section>
      <Footer />
    </div>
  );
};

